import { ResultsState, ResultsType, ValidationStatuses } from 'enums'

import { Result } from 'services/results/types'
import { formatDate } from 'utils/date'
import type { ColumnForExport } from 'utils/export'

import { getExportRequestName, getRequestTypeDisplay } from './utils'

export type ResultColumnForExport = ColumnForExport<Result>

const getValidationStatusDisplay = (validationStatus?: ValidationStatuses): string => {
  if (!validationStatus) return '-'

  switch (validationStatus) {
    case ValidationStatuses.Verified:
      return 'Verified'
    case ValidationStatuses.NotChecked:
      return 'Not checked'
    case ValidationStatuses.AutoCheckInProgress:
      return 'Auto-check in progress'
    case ValidationStatuses.AutoCheckPassed:
      return 'Auto-check passed'
    case ValidationStatuses.AutoCheckFailed:
      return 'Auto-check failed'
    default:
      return validationStatus
  }
}

export const columnsForFilesAndFolders: ResultColumnForExport[] = [
  {
    title: 'Status',
    render: data => data.status
  },
  {
    title: 'Dataset / File',
    render: data => getExportRequestName(data)
  },
  {
    title: 'Requester workspace',
    render: data => data.dataAccessRequest?.requesterWorkspaceName || ''
  },
  {
    title: 'Requested by',
    render: data => data.requestingUser?.email || ''
  },
  {
    title: 'Request type',
    render: data => getRequestTypeDisplay(data.requestType)
  },
  {
    title: 'Request date',
    render: data => formatDate(data.createdAt)
  },
  {
    title: 'Decided by',
    render: data => {
      // Show "-" for pending requests
      if (data.status === ResultsState.PendingVerification) {
        return '-'
      }
      return data.isAutomaticApproved ? 'Automation' : data.verifiedBy?.email || '-'
    }
  },
  {
    title: 'Decision date',
    render: data => {
      // Show "-" for pending requests
      if (data.status === ResultsState.PendingVerification) {
        return '-'
      }
      // Show formatted date for non-pending requests with verifiedAt
      return data.verifiedAt ? formatDate(data.verifiedAt) : '-'
    }
  },
  {
    title: 'Validation status',
    render: data => getValidationStatusDisplay(data.validationStatus)
  },
  {
    title: 'Workspace name',
    render: data => data.workspaceName || ''
  }
]

export const columnsForAnalysis: ResultColumnForExport[] = [
  {
    title: 'Status',
    render: data => data.status
  },
  {
    title: 'Analysis name',
    render: data => data.description
  },
  {
    title: 'Requested by',
    render: data => data.requestingUser?.email || ''
  },
  {
    title: 'Request date',
    render: data => formatDate(data.createdAt)
  },
  {
    title: 'Decided by',
    render: data => {
      // Show "-" for pending requests
      if (data.status === ResultsState.PendingVerification) {
        return '-'
      }
      return data.isAutomaticApproved ? 'Automation' : data.verifiedBy?.email || '-'
    }
  },
  {
    title: 'Decision date',
    render: data => {
      // Show "-" for pending requests
      if (data.status === ResultsState.PendingVerification) {
        return '-'
      }
      // Show formatted date for non-pending requests with verifiedAt
      return data.verifiedAt ? formatDate(data.verifiedAt) : '-'
    }
  },
  {
    title: 'Validation status',
    render: data => getValidationStatusDisplay(data.validationStatus)
  },
  {
    title: 'Workspace name',
    render: data => data.workspaceName || ''
  }
]

export const getCsvColumns = (resultType: ResultsType): ResultColumnForExport[] =>
  resultType === ResultsType.FilesFolders ? columnsForFilesAndFolders : columnsForAnalysis
